<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUnitConfigurationsApi } from '@/composables/useUnitConfigurationsApi';
import type { UnitConfiguration } from '@/typings/unitConfigurations/UnitConfiguration';

interface SizeOption {
  value: string;
  label: string;
}

interface SizeSearchFieldProps {
  modelValue?: string[] | null
  label?: string
  placeholder?: string
}

const props = withDefaults(defineProps<SizeSearchFieldProps>(), {
  modelValue: null,
  label: '',
  placeholder: ''
});

const emit = defineEmits<{
  'update:modelValue': [value: string[] | null]
}>();

const { t, locale } = useI18n();
const { getAllUnitConfigurations } = useUnitConfigurationsApi();

const sizes = ref<SizeOption[]>([]);
const unitConfigurations = ref<UnitConfiguration[]>([]);

const initializeSizes = async () => {
  try {
    unitConfigurations.value = await getAllUnitConfigurations();
    unitConfigurations.value = unitConfigurations.value.sort((a, b) => a.order - b.order);
    
    sizes.value = [
      { value: '', label: t('forms.rentalFilters.size') },
      ...unitConfigurations.value.map(config => ({
        value: config.id.toString(),
        label: locale.value === 'fr-CA' ? config.name : config.nameEn
      }))
    ];
  } catch (error) {
    console.error('Failed to load unit configurations:', error);
    sizes.value = [{ value: '', label: t('forms.rentalFilters.size') }];
  }
};

const isOpen = ref(false);
const searchTerm = ref('');
const inputRef = ref<HTMLInputElement>();
const containerRef = ref<HTMLDivElement>();

// Grandeurs sélectionnées calculées
const selectedSizes = computed(() => {
  if (!props.modelValue || props.modelValue.length === 0) {
    return [];
  }
  return sizes.value.filter(size => props.modelValue!.includes(size.value));
});

// Vérifier si une option est sélectionnée
const isSelected = (sizeValue: string) => {
  // Si c'est l'option par défaut (valeur vide), elle est cochée quand aucune taille n'est sélectionnée
  if (sizeValue === '') {
    return !props.modelValue || props.modelValue.length === 0;
  }
  return props.modelValue?.includes(sizeValue) || false;
};

// Affichage de la valeur dans l'input
const displayValue = computed(() => {
  if (selectedSizes.value.length === 0) {
    return searchTerm.value || t('forms.rentalFilters.size');
  }
  if (selectedSizes.value.length === 1) {
    return selectedSizes.value[0].label;
  }
  return t('forms.rentalFilters.selectionCount', selectedSizes.value.length);
});

// Filtrage des grandeurs basé sur la recherche
const filteredSizes = computed(() => {
  if (!searchTerm.value) {
    return sizes.value;
  }
  return sizes.value.filter(size => {
    return size.label.toLowerCase().includes(searchTerm.value.toLowerCase());
  });
});

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    searchTerm.value = '';
    setTimeout(() => {
      inputRef.value?.focus();
    }, 0);
  }
};

const toggleSize = (size: SizeOption) => {
  // Si c'est l'option par défaut (valeur vide), effacer la sélection
  if (size.value === '') {
    emit('update:modelValue', null);
    closeDropdown();
    return;
  }
  
  const currentSelection = props.modelValue || [];
  let newSelection: string[];
  
  if (currentSelection.includes(size.value)) {
    // Désélectionner
    newSelection = currentSelection.filter(value => value !== size.value);
  } else {
    // Sélectionner
    newSelection = [...currentSelection, size.value];
  }
  
  emit('update:modelValue', newSelection.length > 0 ? newSelection : null);
};

const clearSelection = () => {
  emit('update:modelValue', null);
  searchTerm.value = '';
};

const handleInputChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  searchTerm.value = target.value;
  if (!isOpen.value) {
    isOpen.value = true;
  }
};

const closeDropdown = () => {
  isOpen.value = false;
  searchTerm.value = '';
};

// Fermer le dropdown quand on clique à l'extérieur
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Element;
  // Fermer seulement si on clique en dehors de ce composant spécifique
  if (containerRef.value && !containerRef.value.contains(target)) {
    closeDropdown();
  }
};

onMounted(() => {
  // Initialiser les options de grandeur
  initializeSizes();
  
  // Écouter les clics à l'extérieur avec un délai pour éviter les conflits
  setTimeout(() => {
    document.addEventListener('click', handleClickOutside);
  }, 100);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div ref="containerRef" class="search-field">
    <label class="field-label">
      {{ props.label || t('forms.rentalFilters.sizeTitle') }}
    </label>
    <div class="input-wrapper">
      <input
        ref="inputRef"
        type="text"
        class="search-input"
        :value="displayValue"
        :placeholder="props.placeholder || t('forms.rentalFilters.size')"
        @click="toggleDropdown"
        @input="handleInputChange" />
      <button
        type="button"
        class="dropdown-toggle"
        @click="toggleDropdown">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 448 512"
          :class="{ 'rotated': isOpen }">
          <path d="M201.4 374.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 306.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z" />
        </svg>
      </button>
      <button
        v-if="selectedSizes.length > 0"
        type="button"
        class="clear-button"
        @click="clearSelection">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 384 512">
          <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3l105.4 105.4c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256l105.3-105.4z" />
        </svg>
      </button>
    </div>

    <!-- Dropdown des options -->
    <div
      v-if="isOpen"
      class="dropdown-menu">
      <div
        v-if="filteredSizes.length === 0"
        class="no-results">
        {{ t('forms.rentalFilters.noSizesFound') }}
      </div>
      <label
        v-for="size in filteredSizes"
        :key="size.value"
        class="dropdown-item checkbox-item">
        <input
          type="checkbox"
          :checked="isSelected(size.value)"
          @change="toggleSize(size)"
          class="checkbox" />
        <span class="checkmark"></span>
        <span class="label-text">{{ size.label }}</span>
      </label>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import './search-field-styles.scss';
</style>